import React, { useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import TenkaiMdBoardMapCard from './TenkaiMdBoardMapCard';
import { FormatDate, isEmpty } from '../../modules/appLibrary';

const DEFAULT_AXES = {
    horizontal: { column: 'SSDX_SHOPYMDW', label: '展開週' },
    vertical: { column: 'PDT_ITMCITEMCD', label: 'アイテム' }
};

const CARD_WIDTH = 261;
const CARD_GAP = 6;
const ROW_NUMBER_WIDTH = 30;
const DEFAULT_CARD_COLUMNS_PER_ROW = 5;
const MIN_CARD_COLUMNS_PER_BOARD = 2;
const CELL_HORIZONTAL_PADDING = 16;
const BOARD_BORDER_COLOR = '#aab7b1';
const BOARD_HEADER_BG = '#9fafaa';
const BOARD_SUMMARY_BG = '#eef4f2';
const BOARD_CELL_SUMMARY_BG = '#eef5fb';
const BOARD_TITLE_HEIGHT = 34;
const BOARD_SUMMARY_HEIGHT = 30;
const BOARD_DETAIL_ROW_HEIGHT = 108;
const BOARD_SUBTOTAL_HEIGHT = BOARD_SUMMARY_HEIGHT;

const { useEffect, useRef } = React;

function getCellWidthFromColumns(columnCount) {
    const normalized = Number(columnCount);
    const count = Number.isFinite(normalized) && normalized > 0 ? normalized : 1;
    return (count * CARD_WIDTH) + (CARD_GAP * Math.max(count - 1, 0)) + CELL_HORIZONTAL_PADDING;
}

function chunkItems(items, chunkSize) {
    const source = Array.isArray(items) ? items.filter(item => !isEmpty(item)) : [];
    const size = Number(chunkSize);
    const normalizedSize = Number.isFinite(size) && size > 0 ? Math.floor(size) : DEFAULT_CARD_COLUMNS_PER_ROW;
    const result = [];

    for (let index = 0; index < source.length; index += normalizedSize) {
        result.push(source.slice(index, index + normalizedSize));
    }

    return result;
}

function getMapCardKey(item) {
    const keys = [
        normalizeText(resolveValue(item, ['SSD_NO'])),
        normalizeText(resolveValue(item, ['PDT_SYOHINCD', 'PDT_SyohinCD', 'PDT_HINBAN', 'hinban', 'productCode'])),
        normalizeText(resolveValue(item, ['SSDX_KIKAKUNO'])),
        normalizeText(resolveValue(item, ['SSDX_SHOPYMD'])),
        normalizeText(resolveValue(item, ['SSDM_COLORCD'])),
        normalizeText(resolveValue(item, ['SSDM_SIZECD']))
    ].filter((value) => !isEmpty(value));

    return keys.length === 0 ? '' : keys.join('|');
}

function buildBoardCellRows(board) {
    const result = {};
    const sourceBoard = board || {};
    const rows = Array.isArray(sourceBoard.rows) ? sourceBoard.rows : [];
    const columns = Array.isArray(sourceBoard.columns) ? sourceBoard.columns : [];
    const cellMap = sourceBoard.cellMap instanceof Map ? sourceBoard.cellMap : new Map();

    rows.forEach((row) => {
        columns.forEach((column) => {
            const cellKey = `${row.key}||${column.key}`;
            const cellItems = cellMap.get(cellKey) || [];
            if (cellItems.length > 0) {
                result[cellKey] = chunkItems(cellItems, DEFAULT_CARD_COLUMNS_PER_ROW);
            }
        });
    });

    return result;
}

function getCellMaxRowLength(rowGroups) {
    if (!Array.isArray(rowGroups) || rowGroups.length === 0) {
        return 0;
    }

    return rowGroups.reduce((maxLength, row) => {
        const length = Array.isArray(row) ? row.length : 0;
        return length > maxLength ? length : maxLength;
    }, 0);
}

function findCardLocationInCellRows(cellRows, cardKey) {
    if (isEmpty(cellRows) || isEmpty(cardKey)) {
        return null;
    }

    const cellKeys = Object.keys(cellRows);
    for (let cellIndex = 0; cellIndex < cellKeys.length; cellIndex++) {
        const cellKey = cellKeys[cellIndex];
        const rowGroups = Array.isArray(cellRows[cellKey]) ? cellRows[cellKey] : [];
        for (let rowIndex = 0; rowIndex < rowGroups.length; rowIndex++) {
            const rowItems = Array.isArray(rowGroups[rowIndex]) ? rowGroups[rowIndex] : [];
            for (let itemIndex = 0; itemIndex < rowItems.length; itemIndex++) {
                if (getMapCardKey(rowItems[itemIndex]) === cardKey) {
                    return {
                        cellKey,
                        rowIndex,
                        itemIndex,
                        item: rowItems[itemIndex]
                    };
                }
            }
        }
    }

    return null;
}

function calculateRowInsertIndex(rowElement, clientX, itemCount) {
    const count = Number(itemCount);
    const normalizedCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    if (!rowElement || normalizedCount === 0) {
        return 0;
    }

    const rect = rowElement.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const unitWidth = CARD_WIDTH + CARD_GAP;

    for (let index = 0; index < normalizedCount; index++) {
        const center = (index * unitWidth) + (CARD_WIDTH / 2);
        if (relativeX < center) {
            return index;
        }
    }

    return normalizedCount;
}

function moveCardWithinCellRows(cellRows, cellKey, cardKey, targetRowIndex, targetInsertIndex) {
    if (isEmpty(cellRows) || isEmpty(cellKey) || isEmpty(cardKey)) {
        return null;
    }

    const sourceRows = Array.isArray(cellRows[cellKey]) ? cellRows[cellKey] : [];
    if (sourceRows.length === 0) {
        return null;
    }

    let sourceRowIndex = -1;
    let sourceItemIndex = -1;
    let sourceItem = null;

    for (let rowIndex = 0; rowIndex < sourceRows.length; rowIndex++) {
        const rowItems = Array.isArray(sourceRows[rowIndex]) ? sourceRows[rowIndex] : [];
        for (let itemIndex = 0; itemIndex < rowItems.length; itemIndex++) {
            if (getMapCardKey(rowItems[itemIndex]) === cardKey) {
                sourceRowIndex = rowIndex;
                sourceItemIndex = itemIndex;
                sourceItem = rowItems[itemIndex];
                break;
            }
        }
        if (!isEmpty(sourceItem)) {
            break;
        }
    }

    if (sourceRowIndex < 0 || sourceItemIndex < 0 || isEmpty(sourceItem)) {
        return null;
    }

    const sameRow = sourceRowIndex === targetRowIndex;
    if (sameRow && Array.isArray(sourceRows[sourceRowIndex]) && sourceRows[sourceRowIndex].length <= 1) {
        return cellRows;
    }

    const nextRows = sourceRows.map((row) => (Array.isArray(row) ? row.slice() : []));
    nextRows[sourceRowIndex].splice(sourceItemIndex, 1);

    const sourceRowEmpty = nextRows[sourceRowIndex].length === 0;
    if (sourceRowEmpty) {
        nextRows.splice(sourceRowIndex, 1);
    }

    if (nextRows.length === 0) {
        return {
            ...cellRows,
            [cellKey]: [[sourceItem]]
        };
    }

    let adjustedTargetRowIndex = Number(targetRowIndex);
    if (!Number.isFinite(adjustedTargetRowIndex)) {
        adjustedTargetRowIndex = nextRows.length - 1;
    }

    if (sourceRowEmpty && sourceRowIndex < adjustedTargetRowIndex) {
        adjustedTargetRowIndex -= 1;
    }

    adjustedTargetRowIndex = Math.max(0, Math.min(adjustedTargetRowIndex, nextRows.length - 1));

    const targetRow = Array.isArray(nextRows[adjustedTargetRowIndex]) ? nextRows[adjustedTargetRowIndex].slice() : [];
    let adjustedInsertIndex = Number(targetInsertIndex);
    if (!Number.isFinite(adjustedInsertIndex)) {
        adjustedInsertIndex = targetRow.length;
    }

    if (sameRow && sourceItemIndex < adjustedInsertIndex) {
        adjustedInsertIndex -= 1;
    }

    adjustedInsertIndex = Math.max(0, Math.min(adjustedInsertIndex, targetRow.length));
    targetRow.splice(adjustedInsertIndex, 0, sourceItem);
    nextRows[adjustedTargetRowIndex] = targetRow;

    return {
        ...cellRows,
        [cellKey]: nextRows
    };
}

function getBoardMinimumColumns(itemCount) {
    const normalized = Number(itemCount);
    if (!Number.isFinite(normalized) || normalized <= 0) {
        return 1;
    }

    if (normalized <= 2) {
        return 2;
    }

    return Math.min(normalized, DEFAULT_CARD_COLUMNS_PER_ROW);
}

function normalizeText(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
}

function resolveAxis(axis, fallback) {
    const column = (normalizeText(axis?.column) || fallback.column || "").toUpperCase();
    const label = normalizeText(axis?.label) || fallback.label;
    return { column, label };
}

function resolveBoardAxes(boardAxes) {
    const horizontal = resolveAxis(boardAxes?.horizontal, DEFAULT_AXES.horizontal);
    const vertical = resolveAxis(boardAxes?.vertical, DEFAULT_AXES.vertical);

    return { horizontal, vertical };
}

function resolveValue(item, keys) {
    if (isEmpty(item) || isEmpty(keys)) return '';

    const normalizedKeys = keys
        .map(key => normalizeText(key).toUpperCase())
        .filter(key => !isEmpty(key));

    if (normalizedKeys.length === 0) return '';

    const entries = Object.keys(item);
    for (let i = 0; i < entries.length; i++) {
        const itemKey = normalizeText(entries[i]).toUpperCase();
        if (isEmpty(itemKey)) continue;
        if (normalizedKeys.includes(itemKey)) {
            const value = item[entries[i]];
            if (!isEmpty(value)) {
                return value;
            }
        }
    }

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (Object.prototype.hasOwnProperty.call(item, key) && !isEmpty(item[key])) {
            return item[key];
        }
    }

    return '';
}

function getAxisValue(item, axis) {
    if (isEmpty(item) || isEmpty(axis)) return '';

    const column = normalizeText(axis?.column).toUpperCase();
    const keys = [];

    [column].filter(value => !isEmpty(value)).forEach((columnValue) => {
        [
            columnValue,
            columnValue.toLowerCase(),
            columnValue.replace(/_/g, ''),
            columnValue.replace(/\./g, '')
        ].forEach((key) => {
            if (!isEmpty(key) && !keys.includes(key)) {
                keys.push(key);
            }
        });
    });

    return resolveValue(item, keys);
}

function getAxisDisplayValue(item, axis) {
    if (isEmpty(item) || isEmpty(axis)) return '';

    const column = String(axis.column || '').trim().toUpperCase();
    const rawValue = getAxisValue(item, axis);
    const displayValue = resolveValue(item, [
        `${column}_NM`,
        `${column}NM`,
        `${column}_NAME`,
        `${column}NAME`,
        `${column}_CDNM`,
        `${column}CDNM`
    ]);

    return formatAxisValue(axis, isEmpty(displayValue) ? rawValue : displayValue);
}

function getAxisShopDateValue(item, axis) {
    if (isEmpty(item) || isEmpty(axis)) return '';

    const column = normalizeText(axis?.column).toUpperCase();
    if (column === 'PDTMD_SHOPYMD' || column === 'SSDX_SHOPYMD' || column.includes('DATE')) {
        return getAxisValue(item, axis);
    }

    if (
        column === 'PDTMD_SHOPYMDM' ||
        column === 'SSDX_SHOPYMDM' ||
        column.includes('MONTH') ||
        isWeekAxis(axis)
    ) {
        return resolveValue(item, ['PDTMD_SHOPYMD', 'SSDX_SHOPYMD']);
    }

    return '';
}

function isItemAxis(axis) {
    const column = String(axis?.column || '').trim().toUpperCase();
    return column === 'PDT_ITMCITEMCD' || column === 'PDT_AITEMCITEMCD' || column === 'ITEM';
}

function getVerticalAxisDisplayValue(item, axis) {
    if (!isItemAxis(axis)) {
        return getAxisDisplayValue(item, axis);
    }

    const itemCode = normalizeText(getAxisValue(item, axis));
    const itemName = normalizeText(resolveValue(item, [
        'PDT_ITMCITEMCD_NAME',
        'PDT_ITMCITEMCD_NM',
        'PDT_AITEMCITEMCD_NAME',
        'PDT_AITEMCITEMCD_NM',
        'ITEM_NAME',
        'ITEM_NM'
    ]));

    if (isEmpty(itemCode)) {
        return itemName;
    }

    if (isEmpty(itemName)) {
        return itemCode;
    }

    return `${itemCode}:${itemName}`;
}

function formatAxisValue(axis, value) {
    if (isEmpty(value)) return '';

    const column = String(axis?.column || '').toUpperCase();
    const text = String(value).trim();

    if (column === 'SSDX_SHOPYMDM' || column.includes('MONTH')) {
        if (text.endsWith('月')) {
            return text;
        }

        const monthText = text.replace(/[^0-9]/g, '');
        return isEmpty(monthText) ? text : `${Number(monthText)}月`;
    }

    if (column === 'SSDX_SHOPYMD' || column === 'SSDNY_CENTER' || column.includes('DATE')) {
        return FormatDate(value);
    }

    return text;
}

function isWeekAxis(axis) {
    const column = normalizeText(axis?.column).toUpperCase();
    return column === 'PDTMD_SHOPYMDW' || column === 'SSDX_SHOPYMDW' || column.includes('WEEK');
}

function parseUtcDate(value) {
    if (isEmpty(value)) return null;

    const match = String(value).trim().match(/^(\d{4})[-\/]?(\d{1,2})[-\/]?(\d{1,2})/);
    if (!match) return null;

    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return Number.isNaN(date.getTime()) ? null : date;
}

function getIsoWeekStart(year, weekNumber) {
    const normalizedYear = Number(year);
    const normalizedWeek = Number(weekNumber);
    if (!Number.isInteger(normalizedYear) || !Number.isInteger(normalizedWeek) || normalizedWeek < 1 || normalizedWeek > 53) {
        return null;
    }

    const januaryFourth = new Date(Date.UTC(normalizedYear, 0, 4));
    const januaryFourthDay = januaryFourth.getUTCDay() || 7;
    const firstMonday = new Date(januaryFourth);
    firstMonday.setUTCDate(januaryFourth.getUTCDate() - januaryFourthDay + 1);
    firstMonday.setUTCDate(firstMonday.getUTCDate() + ((normalizedWeek - 1) * 7));
    return firstMonday;
}

function formatMonthDay(date) {
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
}

function getWeekPeriodLabel(item, axis, weekValue) {
    if (!isWeekAxis(axis)) return '';

    const weekMatch = normalizeText(weekValue).match(/(\d{1,2})\s*W?/i);
    if (!weekMatch) return '';

    const shopDate = parseUtcDate(resolveValue(item, ['PDTMD_SHOPYMD', 'SSDX_SHOPYMD']));
    let startDate = null;
    if (shopDate) {
        const isoDay = shopDate.getUTCDay() || 7;
        startDate = new Date(shopDate);
        startDate.setUTCDate(shopDate.getUTCDate() - isoDay + 1);
    } else {
        const year = resolveValue(item, ['SSD_YEAR', 'PDT_YEAR']);
        startDate = getIsoWeekStart(year, Number(weekMatch[1]));
    }

    if (!startDate) return '';

    const endDate = new Date(startDate);
    endDate.setUTCDate(startDate.getUTCDate() + 6);
    return `${formatMonthDay(startDate)}～${formatMonthDay(endDate)}`;
}

function getAxisSortKey(axis, value) {
    const text = normalizeText(value);
    if (isEmpty(text)) {
        return { rank: 3, value: '' };
    }

    const column = String(axis?.column || '').trim().toUpperCase();
    if (column === 'SSDX_SHOPYMD' || column.includes('DATE')) {
        const dateValue = new Date(text);
        if (!Number.isNaN(dateValue.getTime())) {
            return { rank: 0, value: dateValue.getTime() };
        }
    }

    if (column === 'SSDX_SHOPYMDW' || column.includes('WEEK')) {
        const match = text.match(/(\d{1,2})\s*W/i);
        if (match) {
            return { rank: 0, value: Number(match[1]) };
        }
    }

    if (column === 'SSDX_SHOPYMDM' || column.includes('MONTH')) {
        const monthMatch = text.match(/(\d{1,2})/);
        if (monthMatch) {
            return { rank: 0, value: Number(monthMatch[1]) };
        }
    }

    return { rank: 1, value: text.toUpperCase() };
}

function compareAxisValues(axis, left, right) {
    const leftKey = getAxisSortKey(axis, left);
    const rightKey = getAxisSortKey(axis, right);

    if (leftKey.rank !== rightKey.rank) {
        return leftKey.rank - rightKey.rank;
    }

    if (typeof leftKey.value === 'number' && typeof rightKey.value === 'number') {
        return leftKey.value - rightKey.value;
    }

    return String(leftKey.value).localeCompare(String(rightKey.value), 'ja', { numeric: true, sensitivity: 'base' });
}

function normalizeNumberText(value) {
    if (isEmpty(value)) return '';

    return String(value)
        .trim()
        .replace(/,/g, '')
        .replace(/[^\d.-]/g, '');
}

function toNumber(value) {
    const normalized = normalizeNumberText(value);
    if (isEmpty(normalized)) return 0;

    const numberValue = Number(normalized);
    return Number.isFinite(numberValue) ? numberValue : 0;
}

function formatNumber(value) {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
        return '0';
    }

    return new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(numberValue);
}

function summarizeItems(items) {
    const source = Array.isArray(items) ? items : [];

    return {
        count: source.length,
        quantity: source.reduce((sum, item) => sum + toNumber(resolveValue(item, ['SSDM_MDHATCHUSU'])), 0),
        price: source.reduce((sum, item) => {
            const quantity = toNumber(resolveValue(item, ['SSDM_MDHATCHUSU']));
            const unitPrice = toNumber(resolveValue(item, ['SSDX_PRICE']));
            return sum + (unitPrice * quantity);
        }, 0),
        cost: source.reduce((sum, item) => {
            const quantity = toNumber(resolveValue(item, ['SSDM_MDHATCHUSU']));
            const unitCost = toNumber(resolveValue(item, ['PDTMD_YOTEIGENKA', 'PDT_KARI_GENKA', 'PDTMD_KARI_GENKA']));
            return sum + (unitCost * quantity);
        }, 0)
    };
}

function buildBoardStructure(items, axes, axisItems) {
    const source = Array.isArray(items) ? items.filter(item => !isEmpty(item)) : [];
    const axisSource = Array.isArray(axisItems) ? axisItems.filter(item => !isEmpty(item)) : [];
    const horizontal = axes.horizontal || DEFAULT_AXES.horizontal;
    const vertical = axes.vertical || DEFAULT_AXES.vertical;
    const rows = [];
    const columns = [];
    const rowMap = new Map();
    const columnMap = new Map();
    const cellMap = new Map();

    function ensureAxisEntries(item) {
        const rowValueRaw = getAxisValue(item, vertical);
        const colValueRaw = getAxisValue(item, horizontal);

        if (isEmpty(rowValueRaw) || isEmpty(colValueRaw)) {
            return null;
        }

        const rowKey = normalizeText(rowValueRaw);
        const colKey = normalizeText(colValueRaw);
        if (isEmpty(rowKey) || isEmpty(colKey)) {
            return null;
        }

        const rowShopDate = getAxisShopDateValue(item, vertical);
        if (!rowMap.has(rowKey)) {
            const rowEntry = {
                key: rowKey,
                value: rowValueRaw,
                label: getVerticalAxisDisplayValue(item, vertical),
                shopDate: rowShopDate
            };
            rowMap.set(rowKey, rowEntry);
            rows.push(rowEntry);
        } else if (isEmpty(rowMap.get(rowKey).shopDate) && !isEmpty(rowShopDate)) {
            rowMap.get(rowKey).shopDate = rowShopDate;
        }

        const columnShopDate = getAxisShopDateValue(item, horizontal);
        if (!columnMap.has(colKey)) {
            const columnEntry = {
                key: colKey,
                value: colValueRaw,
                label: getAxisDisplayValue(item, horizontal),
                periodLabel: getWeekPeriodLabel(item, horizontal, colValueRaw),
                shopDate: columnShopDate
            };
            columnMap.set(colKey, columnEntry);
            columns.push(columnEntry);
        } else if (isEmpty(columnMap.get(colKey).shopDate) && !isEmpty(columnShopDate)) {
            columnMap.get(colKey).shopDate = columnShopDate;
        }

        return { rowKey, colKey };
    }

    axisSource.forEach((item) => {
        ensureAxisEntries(item);
    });

    source.forEach((item) => {
        const axisEntry = ensureAxisEntries(item);
        if (isEmpty(axisEntry)) {
            return;
        }

        const rowKey = axisEntry.rowKey;
        const colKey = axisEntry.colKey;
        const cellKey = `${rowKey}||${colKey}`;
        if (!cellMap.has(cellKey)) {
            cellMap.set(cellKey, []);
        }
        cellMap.get(cellKey).push(item);
    });

    rows.sort((left, right) => compareAxisValues(vertical, left.value, right.value));
    columns.sort((left, right) => compareAxisValues(horizontal, left.value, right.value));

    const columnTotals = new Map();

    columns.forEach((col) => {
        const columnItems = rows.reduce((acc, row) => {
            const cellItems = cellMap.get(`${row.key}||${col.key}`) || [];
            return acc.concat(cellItems);
        }, []);
        columnTotals.set(col.key, summarizeItems(columnItems));
    });

    return {
        rows,
        columns,
        cellMap,
        columnTotals,
        itemCount: source.length
    };
}

function renderMetricValue(value) {
    return formatNumber(value);
}

function renderMetricBar(summary, options = {}) {
    const source = summary || {};
    const backgroundColor = options.backgroundColor || BOARD_SUMMARY_BG;
    const height = options.height || BOARD_SUMMARY_HEIGHT;
    const labelFontSize = options.labelFontSize || 9;
    const valueFontSize = options.valueFontSize || 11;
    const labelColor = options.labelColor || '#2f3d39';
    const valueColor = options.valueColor || '#2c3a36';
    const inline = Boolean(options.inline);
    const labels = options.labels || {};
    const labelFontWeight = options.labelFontWeight || 700;
    const borderBottom = options.borderBottom || 'none';

    const items = [
        { key: 'quantity', label: labels.quantity || '数量' },
        { key: 'price', label: labels.price || '上代' },
        { key: 'cost', label: labels.cost || '原価' }
    ];

    return (
        <Box
            display="grid"
            gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            style={{
                backgroundColor,
                borderBottom,
                height,
                minHeight: height,
                boxSizing: 'border-box'
            }}
        >
            {items.map((item, index) => (
                <Box
                    key={item.key}
                    style={{
                        padding: inline ? '1px 6px' : '3px 6px 2px',
                        borderRight: index < items.length - 1 ? `1px solid ${BOARD_BORDER_COLOR}` : 'none',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: inline ? 'row' : 'column',
                        justifyContent: inline ? 'space-between' : 'center',
                        alignItems: inline ? 'center' : 'stretch',
                        height: '100%',
                        minHeight: 0
                    }}
                >
                    <div style={{
                        color: labelColor,
                        fontSize: labelFontSize,
                        fontWeight: labelFontWeight,
                        lineHeight: 1.05,
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flex: inline ? '1 1 auto' : 'none',
                        marginRight: inline ? 8 : 0
                    }}>
                        {item.label}
                    </div>
                    <div style={{
                        fontWeight: 700,
                        color: valueColor,
                        fontSize: valueFontSize,
                        lineHeight: 1.05,
                        whiteSpace: 'nowrap',
                        flex: inline ? '0 0 auto' : 'none',
                        textAlign: 'right'
                    }}>
                        {renderMetricValue(source[item.key])}
                    </div>
                </Box>
            ))}
        </Box>
    );
}

function renderBoardCellMetrics(summary, options = {}) {
    return renderMetricBar(summary, {
        backgroundColor: options.backgroundColor || BOARD_CELL_SUMMARY_BG,
        height: options.height || BOARD_SUBTOTAL_HEIGHT,
        labelFontSize: options.labelFontSize || 9,
        valueFontSize: options.valueFontSize || 10,
        inline: true,
        borderBottom: `1px solid ${BOARD_BORDER_COLOR}`,
        labels: options.labels || {
            quantity: '数量小計',
            price: '上代小計',
            cost: '原価小計'
        }
    });
}

export default function TenkaiMdBoardFrame(props) {
    const visible = Boolean(props.visible);
    const axes = resolveBoardAxes(props.boardAxes || {});
    const board = useMemo(
        () => buildBoardStructure(props.items, axes, props.axisItems),
        [
            props.items,
            props.axisItems,
            axes.horizontal.column,
            axes.horizontal.label,
            axes.vertical.column,
            axes.vertical.label
        ]
    );
    const [cellRows, setCellRows] = useState(() => buildBoardCellRows(board));
    const dragMapCardRef = useRef({ key: '', source: '', cellKey: '' });
    const [dragOverCellKey, setDragOverCellKey] = useState('');
    useEffect(() => {
        setCellRows(buildBoardCellRows(board));
    }, [board]);
    const displayBoard = board;
    const columnWidths = useMemo(() => {
        const minimumSingleColumnWidth = getCellWidthFromColumns(2);

        return displayBoard.columns.map((column) => {
            let widestCellWidth = minimumSingleColumnWidth;

            displayBoard.rows.forEach((row) => {
                const cellKey = `${row.key}||${column.key}`;
                const cellItems = board.cellMap.get(cellKey) || [];
                const rowGroups = Array.isArray(cellRows[cellKey]) && cellRows[cellKey].length > 0
                    ? cellRows[cellKey]
                    : chunkItems(cellItems, DEFAULT_CARD_COLUMNS_PER_ROW);
                const displayColumns = Math.max(getCellMaxRowLength(rowGroups), 2);
                const cellWidth = getCellWidthFromColumns(displayColumns);
                if (cellWidth > widestCellWidth) {
                    widestCellWidth = cellWidth;
                }
            });

            return widestCellWidth;
        });
    }, [board.cellMap, cellRows, displayBoard.columns, displayBoard.rows]);
    const boardWidth = useMemo(() => {
        const calculatedWidth = columnWidths.reduce((sum, width) => sum + width, ROW_NUMBER_WIDTH);
        const minimumColumns = Math.max(MIN_CARD_COLUMNS_PER_BOARD, getBoardMinimumColumns(board?.itemCount || 0));
        const minimumWidth = ROW_NUMBER_WIDTH + getCellWidthFromColumns(minimumColumns);

        return Math.max(calculatedWidth, minimumWidth);
    }, [board?.itemCount, columnWidths]);
    if (!visible || displayBoard.rows.length === 0 || displayBoard.columns.length === 0) {
        return null;
    }

    const gridTemplateColumns = `${ROW_NUMBER_WIDTH}px ${columnWidths.map((width) => `${width}px`).join(' ')}`;

    function getCellRowGroups(cellKey, cellItems) {
        const rowGroups = Array.isArray(cellRows[cellKey]) && cellRows[cellKey].length > 0
            ? cellRows[cellKey]
            : chunkItems(cellItems, DEFAULT_CARD_COLUMNS_PER_ROW);

        return rowGroups;
    }

    function handleDragStart(item, source, event) {
        const cardKey = getMapCardKey(item);
        const normalizedSource = String(source || '');
        const dragLocation = normalizedSource === 'board' && !isEmpty(cardKey)
            ? findCardLocationInCellRows(cellRows, cardKey)
            : null;

        dragMapCardRef.current = {
            key: cardKey,
            source: normalizedSource,
            cellKey: dragLocation ? dragLocation.cellKey : ''
        };

        if (typeof props.onCardDragStart === 'function') {
            props.onCardDragStart(item, source, event);
        }
    }

    function handleDragEnd() {
        dragMapCardRef.current = { key: '', source: '', cellKey: '' };
        setDragOverCellKey('');
        if (typeof props.onCardDragEnd === 'function') {
            props.onCardDragEnd();
        }
    }

    function applyLocalBoardDrop(cellKey, targetRowIndex, targetInsertIndex) {
        const dragInfo = dragMapCardRef.current || {};
        if (dragInfo.source !== 'board' || isEmpty(dragInfo.key) || dragInfo.cellKey !== cellKey) {
            return false;
        }

        setCellRows((prev) => {
            const next = moveCardWithinCellRows(prev, cellKey, dragInfo.key, targetRowIndex, targetInsertIndex);
            return next || prev;
        });
        return true;
    }

    function handleRowDrop(row, column, rowIndex, event) {
        event.preventDefault();
        event.stopPropagation();
        setDragOverCellKey('');

        const cellKey = `${row.key}||${column.key}`;
        const cellItems = board.cellMap.get(cellKey) || [];
        const rowGroups = getCellRowGroups(cellKey, cellItems);
        const targetRowItems = Array.isArray(rowGroups[rowIndex]) ? rowGroups[rowIndex] : [];

        if (applyLocalBoardDrop(
            cellKey,
            rowIndex,
            calculateRowInsertIndex(event.currentTarget, event.clientX, targetRowItems.length)
        )) {
            return;
        }

        if (typeof props.onCardDrop === 'function') {
            props.onCardDrop({
                horizontalColumn: axes.horizontal.column,
                horizontalValue: column.value,
                horizontalLabel: axes.horizontal.label,
                verticalColumn: axes.vertical.column,
                verticalValue: row.value,
                verticalLabel: axes.vertical.label,
                horizontalShopDate: column.shopDate,
                verticalShopDate: row.shopDate,
                shopDateValue: column.shopDate || row.shopDate || '',
                rowKey: row.key,
                columnKey: column.key
            }, event);
        }
    }

    function handleBlankCellDrop(row, column, event) {
        event.preventDefault();
        event.stopPropagation();
        setDragOverCellKey('');

        const cellKey = `${row.key}||${column.key}`;
        const cellItems = board.cellMap.get(cellKey) || [];
        const rowGroups = getCellRowGroups(cellKey, cellItems);
        const targetRowIndex = Math.max(rowGroups.length - 1, 0);
        const targetInsertIndex = rowGroups.length > 0 && Array.isArray(rowGroups[targetRowIndex])
            ? rowGroups[targetRowIndex].length
            : 0;

        if (applyLocalBoardDrop(cellKey, targetRowIndex, targetInsertIndex)) {
            return;
        }

        if (typeof props.onCardDrop === 'function') {
            props.onCardDrop({
                horizontalColumn: axes.horizontal.column,
                horizontalValue: column.value,
                horizontalLabel: axes.horizontal.label,
                verticalColumn: axes.vertical.column,
                verticalValue: row.value,
                verticalLabel: axes.vertical.label,
                horizontalShopDate: column.shopDate,
                verticalShopDate: row.shopDate,
                shopDateValue: column.shopDate || row.shopDate || '',
                rowKey: row.key,
                columnKey: column.key
            }, event);
        }
    }

    function renderRowGroup(row, rowIndex) {
        return (
            <Box
                key={row.key}
                style={{
                    display: 'grid',
                    gridTemplateColumns,
                    gridTemplateRows: 'auto auto',
                    borderBottom: `1px solid ${BOARD_BORDER_COLOR}`
                }}
            >
                <Box
                    style={{
                        gridColumn: '1 / span 1',
                        gridRow: '1 / span 2',
                        borderLeft: `1px solid ${BOARD_BORDER_COLOR}`,
                        borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                        backgroundColor: '#ffffff',
                        boxSizing: 'border-box',
                        display: 'grid',
                        gridTemplateRows: 'auto 1fr',
                        alignSelf: 'stretch',
                        position: 'sticky',
                        left: 0,
                        zIndex: 6
                    }}
                >
                    <Box
                        style={{
                            minHeight: BOARD_SUBTOTAL_HEIGHT,
                            height: BOARD_SUBTOTAL_HEIGHT,
                            borderBottom: `1px solid ${BOARD_BORDER_COLOR}`,
                            backgroundColor: BOARD_CELL_SUMMARY_BG,
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            color: '#2f3d39',
                            fontSize: 12,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.1,
                            padding: '2px 1px',
                            overflow: 'hidden'
                        }}
                    >
                        {row.label || row.value || ''}
                    </Box>
                    <Box
                        style={{
                            minHeight: BOARD_DETAIL_ROW_HEIGHT,
                            backgroundColor: '#ffffff',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px 4px',
                            fontWeight: 700,
                            color: '#2f3d39',
                            fontSize: 12
                        }}
                    >
                        {rowIndex + 1}
                    </Box>
                </Box>

                {displayBoard.columns.map((column, columnIndex) => {
                    const cellKey = `${row.key}||${column.key}`;
                    const cellItems = board.cellMap.get(cellKey) || [];
                    const rowGroups = getCellRowGroups(cellKey, cellItems);
                    const summary = summarizeItems(cellItems);

                    return (
                        <React.Fragment key={cellKey}>
                            <Box
                                style={{
                                    gridColumn: `${columnIndex + 2} / span 1`,
                                    gridRow: '1 / span 1',
                                    borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                                    backgroundColor: BOARD_CELL_SUMMARY_BG,
                                    boxSizing: 'border-box',
                                    height: BOARD_SUBTOTAL_HEIGHT,
                                    minHeight: BOARD_SUBTOTAL_HEIGHT
                                }}
                            >
                                {renderBoardCellMetrics(summary, {
                                    backgroundColor: BOARD_CELL_SUMMARY_BG,
                                    height: BOARD_SUBTOTAL_HEIGHT,
                                    labelFontSize: 12,
                                    valueFontSize: 12,
                                    labels: {
                                        quantity: '数量小計',
                                        price: '上代小計',
                                        cost: '原価小計'
                                    }
                                })}
                            </Box>
                            <Box
                                onDragEnter={() => setDragOverCellKey(cellKey)}
                                onDragLeave={(e) => {
                                    if (!e.currentTarget.contains(e.relatedTarget)) {
                                        setDragOverCellKey('');
                                    }
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => handleBlankCellDrop(row, column, e)}
                                style={{
                                    gridColumn: `${columnIndex + 2} / span 1`,
                                    gridRow: '2 / span 1',
                                    borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                                    backgroundColor: dragOverCellKey === cellKey ? '#f1f8ff' : '#ffffff',
                                    minHeight: BOARD_DETAIL_ROW_HEIGHT,
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <Box
                                    style={{
                                        padding: '8px 8px 10px',
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: CARD_GAP,
                                        width: '100%',
                                        minWidth: getCellWidthFromColumns(Math.max(getCellMaxRowLength(rowGroups), 2)),
                                        minHeight: 0
                                    }}
                                >
                                    {rowGroups.length === 0
                                        ? null
                                        : rowGroups.map((rowItems, rowGroupIndex) => (
                                              <Box
                                                  key={`${cellKey}-row-${rowGroupIndex}`}
                                                  onDragOver={(e) => e.preventDefault()}
                                                  onDrop={(e) => handleRowDrop(row, column, rowGroupIndex, e)}
                                                  style={{
                                                      display: 'flex',
                                                      flexDirection: 'row',
                                                      flexWrap: 'nowrap',
                                                      gap: CARD_GAP,
                                                      justifyContent: 'flex-start',
                                                      alignItems: 'flex-start',
                                                      width: '100%',
                                                      minWidth: '100%'
                                                  }}
                                              >
                                                  {rowItems.map((item, index) => {
                                                      const cardKey = getMapCardKey(item);
                                                      const key = isEmpty(cardKey)
                                                          ? `${cellKey}-row-${rowGroupIndex}-${index}`
                                                          : `${cellKey}::${cardKey}`;

                                                      return (
                                                          <TenkaiMdBoardMapCard
                                                              key={key}
                                                              item={item}
                                                              draggable
                                                              source="board"
                                                              onDragStart={handleDragStart}
                                                              onDragEnd={handleDragEnd}
                                                              onOpenMaster={props.onOpenMaster}
                                                              onOpenProductSub={props.onOpenProductSub}
                                                              onToggleStop={props.onToggleStop}
                                                              onDelete={props.onDelete}
                                                          />
                                                      );
                                                  })}
                                              </Box>
                                          ))}
                                </Box>
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Box>
        );
    }

    return (
        <Box
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: 0,
                flex: '1 1 auto',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff'
            }}
        >
            <Box
                style={{
                    overflow: 'auto',
                    height: '100%',
                    minHeight: 0,
                    backgroundColor: '#ffffff',
                    position: 'relative',
                    boxSizing: 'border-box',
                    zIndex: 2
                }}
            >
                <Box
                    style={{
                        width: `${boardWidth}px`,
                        minWidth: `${boardWidth}px`,
                        backgroundColor: '#ffffff',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box
                        style={{
                            display: 'grid',
                            gridTemplateColumns,
                            position: 'sticky',
                            top: 0,
                            zIndex: 20
                        }}
                    >
                        <Box
                            style={{
                                borderLeft: `1px solid ${BOARD_BORDER_COLOR}`,
                                borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                                borderBottom: `1px solid ${BOARD_BORDER_COLOR}`,
                                backgroundColor: BOARD_HEADER_BG,
                                minHeight: BOARD_TITLE_HEIGHT,
                                boxSizing: 'border-box',
                                position: 'sticky',
                                left: 0,
                                zIndex: 21
                            }}
                        />
                        {displayBoard.columns.map((column, columnIndex) => (
                            <Box
                                key={column.key}
                                style={{
                                    borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                                    borderBottom: `1px solid ${BOARD_BORDER_COLOR}`,
                                    backgroundColor: BOARD_HEADER_BG,
                                    minHeight: BOARD_TITLE_HEIGHT,
                                    color: '#ffffff',
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '2px 8px',
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    lineHeight: 1.05
                                }}
                            >
                                <span style={{ minWidth: 14, marginRight: 8, fontSize: 13 }}>{columnIndex + 1}</span>
                                <span style={{ fontSize: 13 }}>
                                    {[column.label, column.periodLabel].filter(value => !isEmpty(value)).join('  ')}
                                </span>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        style={{
                            display: 'grid',
                            gridTemplateColumns,
                            position: 'sticky',
                            top: BOARD_TITLE_HEIGHT,
                            zIndex: 19
                        }}
                    >
                        <Box
                            style={{
                                gridColumn: '1 / span 1',
                                borderLeft: `1px solid ${BOARD_BORDER_COLOR}`,
                                borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                                borderBottom: `1px solid ${BOARD_BORDER_COLOR}`,
                                backgroundColor: BOARD_SUMMARY_BG,
                                minHeight: BOARD_SUMMARY_HEIGHT,
                                boxSizing: 'border-box',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'sticky',
                                left: 0,
                                zIndex: 21,
                                fontWeight: 700,
                                color: '#2f3d39',
                                fontSize: 12
                            }}
                        >
                            集計
                        </Box>
                {displayBoard.columns.map((column) => {
                    const summary = board.columnTotals.get(column.key) || { count: 0, quantity: 0, price: 0, cost: 0 };
                    return (
                                <Box
                                    key={`summary-${column.key}`}
                                    style={{
                                        borderRight: `1px solid ${BOARD_BORDER_COLOR}`,
                                        borderBottom: `1px solid ${BOARD_BORDER_COLOR}`,
                                        backgroundColor: BOARD_SUMMARY_BG,
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {renderMetricBar(summary, {
                                        backgroundColor: BOARD_SUMMARY_BG,
                                        height: BOARD_SUMMARY_HEIGHT,
                                        labelFontSize: 12,
                                        valueFontSize: 12,
                                        inline: true,
                                        labels: {
                                            quantity: '数量合計',
                                            price: '上代合計',
                                            cost: '原価合計'
                                        }
                                    })}
                                </Box>
                            );
                        })}
                    </Box>

                    {displayBoard.rows.map((row, rowIndex) => renderRowGroup(row, rowIndex))}

                    <Box style={{ height: 8 }} />
                </Box>
            </Box>
        </Box>
    );
}
